import { getApp, getApps, initializeApp } from "firebase/app"
import type { FirebaseApp, FirebaseOptions } from "firebase/app"

const REQUIRED_KEYS = [
  "VITE_FIREBASE_API_KEY",
  "VITE_FIREBASE_AUTH_DOMAIN",
  "VITE_FIREBASE_PROJECT_ID",
  "VITE_FIREBASE_STORAGE_BUCKET",
  "VITE_FIREBASE_MESSAGING_SENDER_ID",
  "VITE_FIREBASE_APP_ID",
] as const

function readConfig(): FirebaseOptions {
  return {
    apiKey: import.meta.env.VITE_FIREBASE_API_KEY,
    authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
    projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
    storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET,
    messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID,
    appId: import.meta.env.VITE_FIREBASE_APP_ID,
  }
}

function missingKeys(): string[] {
  return REQUIRED_KEYS.filter((key) => !import.meta.env[key])
}

/** True when every required Firebase env var is present. */
export function isFirebaseConfigured(): boolean {
  return missingKeys().length === 0
}

/**
 * Dev only: route Firestore and Auth to the local Firebase emulators
 * (`firebase emulators:start`) instead of a real project. Off unless
 * `VITE_FIREBASE_EMULATOR=true`, so it can never affect a real build — the flag
 * is absent in production. See `getDb` and `getFirebaseAuth`.
 */
export function isFirebaseEmulator(): boolean {
  return import.meta.env.VITE_FIREBASE_EMULATOR === "true"
}

/**
 * Initialized lazily so a missing .env surfaces where Firebase is actually
 * used, with an actionable message, rather than crashing on import.
 * Reuses an existing app instance so HMR does not re-initialize.
 */
export function getFirebaseApp(): FirebaseApp {
  if (getApps().length > 0) return getApp()

  const missing = missingKeys()
  if (missing.length > 0) {
    throw new Error(
      `Firebase is not configured. Missing env var(s): ${missing.join(", ")}. ` +
        `Copy app-v1/.env.example to app-v1/.env.local and fill in the values.`
    )
  }

  return initializeApp(readConfig())
}
